import { changePasswordSchema } from "@/api/schemas/auth.schema";
import { Symbol, Wordmark } from "@/components/fornada/brand";
import { TextField } from "@/components/fornada/controls";
import { Display } from "@/components/fornada/primitives";
import { Button } from "@/components/ui/button";
import { http } from "@/lib/http";
import { queryClient } from "@/lib/query-client";
import { useAuth, type User } from "@/providers/auth.provider";
import { useToast } from "@/providers/toast.provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";
import { z } from "zod";

const formSchema = changePasswordSchema
  .extend({ confirmPassword: z.string().min(1, "Confirme a nova senha!") })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "As senhas não conferem!",
    path: ["confirmPassword"],
  });

type FormSchema = z.infer<typeof formSchema>;

/** Troca obrigatória: a senha foi redefinida por um ADMIN. O `ProtectedRoute` manda pra cá. */
const ChangePasswordPage = () => {
  const { user, isLoading, signOut } = useAuth();
  const { toaster } = useToast();
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const { mutate: changePassword, isPending } = useMutation({
    mutationFn: async ({ newPassword }: FormSchema) => {
      const { data } = await http.put<{ message: string; user: User }>(
        "auth/change-password",
        { newPassword },
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data.user);
      toaster(data.message, "success");
    },
    onError: (error) => {
      form.setError("newPassword", { message: error?.message ?? "Algo deu errado!" });
    },
  });

  if (isLoading) return null;
  if (!user) return <Navigate to="/sign-in" replace />;
  if (!user.needToChangePassword) return <Navigate to="/" replace />;

  return (
    <div className="grid min-h-screen w-full min-[960px]:grid-cols-2">
      {/* painel de marca (desktop) */}
      <aside className="hidden flex-col border-r border-fx-line-subtle bg-fx-sunken p-10 min-[960px]:flex">
        <Wordmark size={32} />
        <Display
          as="div"
          size="hero"
          className="mt-auto text-[64px] leading-[54px] tracking-[-0.044em]"
        >
          Campanhas
          <br />
          de pizza da
          <br />
          <span className="text-fx-red-icon">Comunidade</span>
        </Display>
      </aside>

      <main className="flex flex-col px-5 pt-6 pb-6 min-[960px]:justify-center min-[960px]:px-12">
        <div className="flex items-center gap-2.5 min-[960px]:hidden">
          <Symbol size={36} />
          <span className="fx-display text-2xl tracking-[-0.025em]">FORNADA</span>
        </div>

        <form
          onSubmit={form.handleSubmit((d) => changePassword(d))}
          className="mx-auto mt-[72px] flex w-full max-w-md flex-1 flex-col gap-3 min-[960px]:mt-0 min-[960px]:flex-none"
        >
          <span className="fx-label text-[10.5px] tracking-[1.5px] text-fx-red-ink">
            Olá, {user.name.split(" ")[0]}
          </span>
          <Display size="hero" className="min-[960px]:text-[32px] min-[960px]:leading-[30px] min-[960px]:tracking-[-0.03em]">
            Defina sua
            <br className="min-[960px]:hidden" /> nova senha
          </Display>
          <p className="mb-3 text-sm text-fx-muted">
            Sua senha foi redefinida por um administrador. Escolha uma nova senha para
            continuar.
          </p>

          <Controller
            name="newPassword"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Nova senha"
                type="password"
                autoComplete="new-password"
                placeholder="Mínimo de 8 caracteres"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Confirmar senha"
                type="password"
                autoComplete="new-password"
                placeholder="Repita a nova senha"
                error={fieldState.error?.message}
              />
            )}
          />

          <Button type="submit" size="xl" className="mt-2 w-full" disabled={isPending}>
            Salvar nova senha
          </Button>

          <button
            type="button"
            onClick={() => signOut()}
            className="mt-1 text-center text-[13px] font-semibold text-fx-cream hover:text-fx-cream-hover"
          >
            Sair e entrar com outra conta
          </button>
        </form>
      </main>
    </div>
  );
};

export default ChangePasswordPage;
